import {Alert, Box, Button, CircularProgress} from "@mui/material";
import React from "react";
import {useAsync} from "react-async-hook";
import {useAuth} from "react-oidc-context";
import {useNavigate, useParams} from "react-router-dom";
import {OrderInitiation} from "../context/order";
import {useSiteApi} from "../data/sites";
import {getZoneDateTimeNow} from "../dates-utils";
import {StorageLocalizationProvider, useLocalization} from "../i18n";
import {findActiveBookingOnSite} from "./storefront/find-active-booking-on-site";

/**
 * Entry point for the single per-locker QR (`/cubes/{cubeId}`) and the specialized QR (`/cubes/{cubeId}/{slug}`).
 * Resolves the scanned cube to its current site/tenant and either routes a returning customer to their active booking,
 * or enters the site's booking flow while remembering the cube (and slug, if any) so they are recorded on the
 * resulting booking. The slug is passed through blindly — the backend resolves its properties at booking creation and
 * silently ignores an unknown/retired slug, so a bad slug behaves exactly like the plain cube QR.
 *
 * Known limitation: the "route to your active booking" shortcut only works for OIDC-authenticated customers, because it
 * relies on `getMyBookings` (which needs a session). Guests who booked anonymously (name/email/phone, no login) cannot
 * be matched and are sent into a fresh booking flow.
 *
 * Mounted as a top-level route (outside {@link TenantContextProvider}, which derives the tenant from the first path
 * segment), so it wraps itself in its own localization provider.
 */
export function CubeResolver() {
	return (
		<StorageLocalizationProvider>
			<CubeResolverInner />
		</StorageLocalizationProvider>
	);
}

function CubeResolverInner() {
	const {t} = useLocalization();
	const {cubeId, slug} = useParams<{ cubeId: string; slug?: string }>();
	const navigate = useNavigate();
	const auth = useAuth();
	const {resolveCube, getMyBookings} = useSiteApi();

	const {error} = useAsync(async () => {
		// Wait for OIDC to finish restoring the session before resolving: react-oidc-context reports
		// isAuthenticated=false while isLoading=true on first render, so resolving now would send a
		// returning customer into a fresh booking flow instead of their active booking (and navigating
		// with replace unmounts us before auth settles). Re-runs once auth is known (isLoading dep).
		if (!cubeId || auth.isLoading) return;
		const {tenantId, siteId} = await resolveCube(cubeId);

		// Returning customer: route straight to an existing active booking at this site.
		if (auth.isAuthenticated) {
			try {
				const bookings = await getMyBookings({siteId, activeAt: new Date().toISOString()});
				// findActiveBookingOnSite compares instants and re-derives each booking's own zone for the
				// day-unit grace window, so the zone of "now" doesn't affect the result — UTC is fine here.
				const active = findActiveBookingOnSite(bookings, siteId, getZoneDateTimeNow("UTC"));
				if (active) {
					navigate(`/${tenantId}/bookings/${active.id}`, {replace: true});
					return;
				}
			}
			catch (e) {
				// Best-effort — fall through to a fresh booking flow if the lookup fails.
				console.warn("Could not check existing bookings for cube", e);
			}
		}

		// Enter the site flow, carrying the initiation cube (and specialized-QR slug) in navigation state so
		// they land on the created booking. Navigation state is scoped to this entry (no global key), so it
		// can't leak into a later unrelated booking the way a persisted store would.
		const initiation: OrderInitiation = {cubeId, slug};
		navigate(`/${tenantId}/${siteId}`, {replace: true, state: {initiation}});
	}, [cubeId, auth.isAuthenticated, auth.isLoading]);

	if (error) {
		return (
			<Box p={2}>
				<Alert
					severity="error"
					action={<Button component="a" href="/">{t("common.toHomeCta")}</Button>}
				>
					{t("common.cubeNotFound")}
				</Alert>
			</Box>
		);
	}

	return (
		<Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
			<CircularProgress />
		</Box>
	);
}
